import api from '../../../services/api';
import * as purchase from './purchaseInvoiceImportApi';
import * as sales from './salesInvoiceImportApi';
import { normalizePurchaseInvoiceDraft, normalizeSalesInvoiceDraft } from '../../../dto/workflowInvoice';
import { buildPurchaseInvoicePayload, buildSalesInvoicePayload } from '../workflowInvoicePayload';
import type { WorkflowInvoice, WorkflowInvoiceDirection } from '../../../types/workflowInvoice';

export interface InvoiceApprovalInput {
  expectedVersion: number;
  idempotencyKey: string;
  recognitionMonth: string;
  receiptCosts?: Array<{ purchaseInvoiceItemId: string; totalCostPLN: number; reason?: string }>;
  pendingStockSelections?: Array<{ purchaseInvoiceItemId: string; pendingIssueIds: string[]; reason?: string }>;
  /** Audited operational-exception acknowledgement (see operationalExceptionFlow). */
  confirmOperationalException?: boolean;
  operationalExceptionFingerprint?: string;
  operationalExceptionReason?: string;
}
export interface InvoiceApprovalResult {
  invoice: WorkflowInvoice;
  pzDocuments: Array<{ id: string; pzNumber: string; sourceSupplierOrderId: string }>;
  orders: Array<{ id: string; commerciallyFrozen: boolean; coverageState: 'PARTIAL' | 'FULL' }>;
  settlement?: unknown;
  replayed: boolean;
}
export function invoiceWorkflowApi(direction: WorkflowInvoiceDirection) {
  const isPurchase = direction === 'PURCHASE';
  return {
    create: isPurchase ? purchase.createPurchaseInvoiceDraft : sales.createSalesInvoiceDraft,
    fetch: isPurchase ? purchase.fetchPurchaseInvoiceDraft : sales.fetchSalesInvoiceDraft,
    update: isPurchase ? purchase.updatePurchaseInvoiceDraft : sales.updateSalesInvoiceDraft,
    remove: isPurchase ? purchase.deletePurchaseInvoiceDraft : sales.deleteSalesInvoiceDraft,
    attach: isPurchase ? purchase.attachPurchaseInvoiceSourceFile : sales.attachSalesInvoiceSourceFile,
    download: isPurchase ? purchase.downloadPurchaseInvoiceSourceFile : sales.downloadSalesInvoiceSourceFile,
    extract: isPurchase ? purchase.requestPurchaseInvoiceExtraction : sales.requestSalesInvoiceExtraction,
    apply: isPurchase ? purchase.applyPurchaseInvoiceExtraction : sales.applySalesInvoiceExtraction,
    payload: isPurchase ? buildPurchaseInvoicePayload : buildSalesInvoicePayload,
    async approve(id: string, input: InvoiceApprovalInput): Promise<InvoiceApprovalResult> {
      const path = isPurchase ? '/purchase-invoices' : '/invoices/import-drafts';
      const response = await api.post(`${path}/${id}/approve`, input);
      const normalize = isPurchase ? normalizePurchaseInvoiceDraft : normalizeSalesInvoiceDraft;
      return { ...response.data, invoice: normalize(response.data.invoice) };
    },
  };
}

export interface InvoiceReceiptCost {
  purchaseInvoiceItemId: string;
  productName: string;
  quantity: number;
  unit: string;
  invoiceNetPLN: number;
  includedExtraCostPLN: number;
  proposedTotalCostPLN: number;
}
export interface InvoiceApprovalPreview {
  receiptCosts?: InvoiceReceiptCost[];
  receiptCostsIssue?: { code: string; message: string };
  orders: Array<{ id: string; orderNumber?: string; coverageState: 'PARTIAL' | 'FULL' | null; commerciallyFrozen: boolean; lines: Array<{ sourceLineId: string; productName?: string; orderedQuantity: number | null; acceptedQuantity: number | null; remainingQuantity: number | null; unit: string }> }>;
  pendingStockCandidates: Array<{ purchaseInvoiceItemId: string; candidates: Array<{ id: string; wzDocumentId: string; wzNumber?: string; orderId: string; orderNumber?: string; productName?: string; quantity: number; unit: string; sourceSupplierOrderItemId: string | null }> }>;
}
export async function fetchInvoiceApprovalPreview(direction: WorkflowInvoiceDirection, id: string): Promise<InvoiceApprovalPreview> {
  const path = direction === 'PURCHASE' ? '/purchase-invoices' : '/invoices/import-drafts';
  const { data } = await api.get(`${path}/${id}/approval-preview`);
  if (!data || !Array.isArray(data.orders) || !Array.isArray(data.pendingStockCandidates)
    || data.orders.some((order: unknown) => !order || !Array.isArray((order as { lines?: unknown }).lines))
    || data.pendingStockCandidates.some((group: unknown) => !group || !Array.isArray((group as { candidates?: unknown }).candidates))) {
    throw new Error('Niepełny podgląd pokrycia faktury.');
  }
  if (data.receiptCosts !== undefined && (!Array.isArray(data.receiptCosts) || data.receiptCosts.some((row: InvoiceReceiptCost) => (
    !row || typeof row.purchaseInvoiceItemId !== 'string' || typeof row.productName !== 'string'
    || ![row.quantity, row.invoiceNetPLN, row.includedExtraCostPLN, row.proposedTotalCostPLN].every(value => typeof value === 'number' && Number.isFinite(value))
  )))) throw new Error('Niepełny podgląd kosztu przyjęcia.');
  return data;
}
